import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { processDocumentContent, contentToPlainText } from '../utils/documentSanitizer';
import '../styles/DocumentDetail.css';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const STATE_LABELS = {
  ready: { dot: '🟢', label: 'Ready' },
  in_progress: { dot: '🟡', label: 'In Progress' },
  needs_attention: { dot: '🔴', label: 'Needs Attention' },
};

function getStateLabel(state) {
  return STATE_LABELS[state] || { dot: '⚪', label: state || 'Unknown' };
}

/* ── Download helper ──────────────────────────────────────────────────────── */

async function downloadDocument(jobId, format, title) {
  const response = await fetch(`/api/jobs/${jobId}/convert?format=${format}`, {
    credentials: 'include',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('session_token')}`
    }
  });

  if (response.status === 429) {
    throw new Error('rate_limit');
  }
  if (response.status === 500) {
    throw new Error('conversion_failed');
  }
  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Build filename from title — ASCII-safe
  const safeName = title.slice(0, 50).replace(/[^a-zA-Z0-9_-]/g, '_');
  a.download = `${safeName}.${format}`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/* ── Main component ───────────────────────────────────────────────────────── */

export default function DocumentDetail({ doc, onOpenConversation }) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Load preview content when a ready document is selected
  const loadPreview = useCallback(async () => {
    if (!doc?.document?.artifact_id || !doc.job_id) return;

    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);

    try {
      const data = await api.get(`/api/jobs/${doc.job_id}/artifacts/${doc.document.artifact_id}`);
      const processed = processDocumentContent(data.content);
      setPreview(processed);
    } catch (err) {
      console.error('Failed to load document preview:', err);
      setPreviewError('Failed to load document preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [doc?.job_id, doc?.document?.artifact_id]);

  useEffect(() => {
    if (doc?.state === 'ready' && doc?.document) {
      loadPreview();
    } else {
      setPreview(null);
      setPreviewLoading(false);
      setPreviewError(null);
    }
  }, [doc?.job_id, doc?.state, doc?.document, loadPreview]);

  if (!doc) {
    return (
      <div className="doc-detail-empty">
        <p>Select a document from the list to view it.</p>
      </div>
    );
  }

  const stateInfo = getStateLabel(doc.state);
  const isReady = doc.state === 'ready' && doc.document;

  const handleDownload = async (format) => {
    setDownloading(format);
    setDownloadError(null);
    try {
      await downloadDocument(doc.job_id, format, doc.title);
    } catch (err) {
      if (err.message === 'rate_limit') {
        setDownloadError('Too many downloads — please wait a few minutes.');
      } else if (err.message === 'conversion_failed') {
        setDownloadError(`This document can't be converted to ${format.toUpperCase()} right now. Try Word or Text instead.`);
      } else {
        setDownloadError('Download failed. Please try again.');
      }
    } finally {
      setDownloading(null);
    }
  };

  const handleCopy = async () => {
    if (!preview) return;
    // Get plain text from the raw content
    try {
      const artifact = await api.get(`/api/jobs/${doc.job_id}/artifacts/${doc.document.artifact_id}`);
      const plainText = contentToPlainText(artifact.content);
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleOpenConversation = () => {
    if (onOpenConversation) {
      onOpenConversation(doc.conversation_id);
    }
  };

  const handleViewJob = () => {
    navigate(`/job/${doc.job_id}`);
  };

  return (
    <div className="doc-detail">
      {/* Header */}
      <div className="doc-detail-header">
        <div className="doc-detail-title-row">
          <h2 className="doc-detail-name">{doc.title}</h2>
          <span className="doc-detail-status">{stateInfo.dot} {stateInfo.label}</span>
        </div>
        <p className="doc-detail-subtitle">
          {doc.template_name} · Created {formatDate(doc.created_at)}
          {doc.completed_at && ` · Completed ${formatDate(doc.completed_at)}`}
        </p>

        {/* Actions — always visible (mobile can't open right panel) */}
        <div className="doc-detail-actions">
          {isReady && (
            <>
              <button
                className="doc-action-btn doc-action-primary"
                onClick={handleCopy}
                disabled={!preview}
              >
                {copied ? '✓ Copied' : '📋 Copy Text'}
              </button>
              <button
                className="doc-action-btn doc-action-secondary"
                onClick={() => handleDownload('pdf')}
                disabled={downloading === 'pdf'}
              >
                {downloading === 'pdf' ? '⏳' : '📕'} PDF
              </button>
              <button
                className="doc-action-btn doc-action-secondary"
                onClick={() => handleDownload('docx')}
                disabled={downloading === 'docx'}
              >
                {downloading === 'docx' ? '⏳' : '📝'} Word
              </button>
              <button
                className="doc-action-btn doc-action-secondary"
                onClick={() => handleDownload('txt')}
                disabled={downloading === 'txt'}
              >
                {downloading === 'txt' ? '⏳' : '📄'} Text
              </button>
            </>
          )}
          {doc.conversation_id && (
            <button
              className="doc-action-btn doc-action-secondary"
              onClick={handleOpenConversation}
            >
              💬 Open Conversation
            </button>
          )}
          <button
            className="doc-action-btn doc-action-secondary"
            onClick={handleViewJob}
          >
            📦 Job Details
          </button>
        </div>
        {downloadError && (
          <p className="doc-download-error">{downloadError}</p>
        )}
      </div>

      {/* Content area */}
      <div className="doc-content-area">
        {doc.state === 'ready' && doc.document ? (
          previewLoading ? (
            <div className="doc-preview-loading">
              <div className="loading-spinner"></div>
              <p>Loading preview…</p>
            </div>
          ) : previewError ? (
            <div className="doc-preview-error">
              <p>{previewError}</p>
              <button className="doc-retry-btn" onClick={loadPreview}>Try again</button>
            </div>
          ) : preview ? (
            <div className="doc-preview">
              {preview.html ? (
                <div
                  className="doc-preview-content doc-preview-html"
                  dangerouslySetInnerHTML={{ __html: preview.html }}
                />
              ) : (
                <div className="doc-preview-content doc-preview-text">
                  {preview.text}
                </div>
              )}
            </div>
          ) : null
        ) : doc.state === 'ready' && !doc.document ? (
          <div className="doc-state-message">
            <p>This document completed but the deliverable isn't available.</p>
            {doc.conversation_id && (
              <button className="doc-action-btn doc-action-secondary" onClick={handleOpenConversation}>
                💬 Open Conversation
              </button>
            )}
            <p className="doc-state-support">
              Need help? <a href="mailto:support@theideamachine.dev">Contact support</a>
            </p>
          </div>
        ) : doc.state === 'in_progress' ? (
          <div className="doc-state-message">
            <p>We're writing this now. It'll show up here when it's ready.</p>
          </div>
        ) : doc.state === 'needs_attention' ? (
          <div className="doc-state-message">
            <p>Something went wrong with this document.</p>
            {doc.conversation_id && (
              <button className="doc-action-btn doc-action-secondary" onClick={handleOpenConversation}>
                💬 Open Conversation
              </button>
            )}
            <p className="doc-state-support">
              Need help? <a href="mailto:support@theideamachine.dev">Contact support</a>
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
